import AlertDialog from '@components/shared/dialogs/alert-dialog'
import React, { ReactElement, useState } from 'react'
import useAllActions from '@hooks/use-all-actions'
import { Button, InputBase } from '@mui/material'
import { Navigate } from 'react-router-dom'
import { UserType } from '@types'
import { banEmail } from '@store/actions/users'
import { makeStyles } from 'tss-react/mui'

interface Props {
  user: UserType
}

const Ban = ({ user }: Props): ReactElement => {
  const { classes } = useStyles()
  const [email, setEmail] = useState('')
  const [processing, setProcessing] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [banned, setBanned] = useState(false)
  const { setFlashMessage } = useAllActions()

  const submitBan = async () => {
    setProcessing(true)

    try {
      await banEmail(user.email)
      setFlashMessage(`Users email '${user.email}' banished`, 'success')
      setBanned(true)
    } catch (e: any) {
      setFlashMessage(e.message, 'error')
    }

    setEmail('')
    setProcessing(false)
    setConfirmDialogOpen(false)
  }

  const handleBanClicked = () => {
    if (email.trim().toLowerCase() !== user.email.toLowerCase()) {
      setFlashMessage(
        "The email you entered doesn't match the users email",
        'error',
      )
      return
    }

    setConfirmDialogOpen(true)
  }

  if (banned) return <Navigate to={'/'} />

  return (
    <>
      <h3>Ban / Block users email</h3>
      <p>
        This user account will immediately be archived so it no longer works and
        the email address will be added to a banned list so the email address
        can no longer be used to sign up again. Downstream the users enrollment
        in their loyalty account will be deleted.
      </p>
      <p>
        Keep in mind this is not the ultimate solution to blocking a specific
        fraudulent user, as they can make another account, but it adds levels of
        difficulty to take advantage of our system.
      </p>
      <label>Confirm email</label>
      <InputBase
        className={classes.input}
        disabled={processing}
        onChange={(e) => setEmail(e.target.value)}
        value={email}
      />
      <Button disabled={processing} onClick={handleBanClicked}>
        Archive and Ban
      </Button>

      <AlertDialog
        closeTitle={'Cancel'}
        confirmTitle={'Continue'}
        loading={processing}
        message={`Are you sure you want to ban user by email ${user.email}?`}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={submitBan}
        open={confirmDialogOpen}
        title={'Archive and Ban'}
      />
    </>
  )
}

export default Ban

const useStyles = makeStyles()((theme) => ({
  input: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    border: `1px solid #d5d5d5`,
  },
}))
